/**
 * ورودی تاریخ شمسی
 * ==================
 * مدیر ایرانی «۱۴۰۵/۰۵/۲۴» می‌نویسد، نه «2026-08-15». این کامپوننت همان را
 * می‌گیرد و به قالب میلادیِ سرور تبدیل می‌کند.
 *
 * تاریخ میلادیِ معادل هم زیر کادر نشان داده می‌شود؛ در یک بخش حسابداری، کاربر
 * باید بتواند قبل از ثبت مطمئن شود درست فهمیده شده — نه اینکه بعداً در گزارش
 * متوجه شود روز اشتباه ثبت شده است.
 */
import { useEffect, useState } from 'react'
import { CalendarDays, X } from 'lucide-react'
import {
  formatJalali, parseJalaliInput, toLocalIso, toFa, JALALI_MONTHS, toJalali,
} from '../lib/jalali'

export default function JalaliInput({
  value, onChange, allowEmpty = false, className = '',
}: {
  /** مقدار به قالب محلیِ `YYYY-MM-DDTHH:mm` (همان چیزی که به سرور می‌رود) */
  value: string
  onChange: (v: string) => void
  allowEmpty?: boolean
  className?: string
}) {
  const [text, setText] = useState('')
  const [touched, setTouched] = useState(false)

  // مقدار بیرونی که عوض شد، متن را همگام می‌کنیم — مگر کاربر در حال تایپ باشد
  useEffect(() => {
    if (touched) return
    if (!value) { setText(''); return }
    const d = new Date(value)
    setText(Number.isNaN(d.getTime()) ? '' : formatJalali(d))
  }, [value, touched])

  const parsed = parseJalaliInput(text)
  const empty = !text.trim()
  const invalid = !empty && !parsed

  const commit = (raw: string) => {
    setText(raw)
    setTouched(true)
    const d = parseJalaliInput(raw)
    if (d) onChange(toLocalIso(d))
    else if (!raw.trim() && allowEmpty) onChange('')
  }

  const setNow = () => {
    const now = new Date()
    setTouched(false)
    setText(formatJalali(now))
    onChange(toLocalIso(now))
  }

  const shift = (days: number) => {
    const base = parsed || new Date()
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days,
                       base.getHours(), base.getMinutes())
    setTouched(false)
    setText(formatJalali(d))
    onChange(toLocalIso(d))
  }

  const j = parsed ? toJalali(parsed) : null

  return (
    <div className={className}>
      <div className="relative">
        <CalendarDays className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          className={`input num !pr-9 ${invalid ? '!border-down/50' : ''}`}
          dir="ltr"
          placeholder="۱۴۰۵/۰۵/۲۴ ۱۴:۳۰"
          value={text}
          onChange={(e) => commit(e.target.value)}
          onBlur={() => setTouched(false)}
        />
        {text && (
          <button type="button" onClick={() => commit('')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:text-slate-300">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px]">
        <button type="button" onClick={setNow} className="chip !py-0.5 text-[11px] hover:text-white">
          اکنون
        </button>
        <button type="button" onClick={() => shift(-1)} className="chip !py-0.5 text-[11px] hover:text-white">
          دیروز
        </button>
        <button type="button" onClick={() => shift(-7)} className="chip !py-0.5 text-[11px] hover:text-white">
          هفته پیش
        </button>

        {invalid ? (
          <span className="text-down">تاریخ نامعتبر است</span>
        ) : parsed && j ? (
          // تأیید دیداری: هم به حروف و هم میلادی، تا اشتباه تایپی دیده شود
          <span className="text-slate-500">
            {toFa(j.jd)} {JALALI_MONTHS[j.jm - 1]} {toFa(j.jy)}
            <span className="mx-1.5 text-slate-700">·</span>
            <span dir="ltr">{parsed.toISOString().slice(0, 10)}</span>
          </span>
        ) : allowEmpty ? (
          <span className="text-slate-600">خالی یعنی بدون محدودیت</span>
        ) : null}
      </div>
    </div>
  )
}
